import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Wifi, WifiOff } from 'lucide-react';
import { usePaymentPolling } from '../../hooks/usePaymentPolling';
import { useModalState } from '../../hooks/useModalState';
import { useCounterState } from '../../hooks/useSalesInfo';

import { sendToWebhook, checkPaymentStatus, validateEmail } from '../../services/paymentService';
import { SEOHead } from './SEOHead';
import { ScoreBoard } from './ScoreBoard';
import { BooksSection } from './BooksSection';
import { PurchaseModal } from './PurchaseModal';
import { Celebration } from './Celebration';
import { PROCESSING_DURATION, COPY_FEEDBACK_DURATION, ANIMATION_DURATION } from '../../data/constants';

const EbookLandingPageProduction = () => {

  // Estados principais
  const { sales, setSales, incrementSales, changedSide, clearChangedSide } = useCounterState();
  const [lastMessage, setLastMessage] = useState("Este livro mudou completamente minha visão política!");
  const [animatingNumber, setAnimatingNumber] = useState(null);
  const [isConnected, setIsConnected] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  
  // Hooks customizados
  const { startPolling, stopPolling } = usePaymentPolling();
  const { state: modalState, updateState: updateModalState, resetState: resetModalState } = useModalState();
  
  // Refs para intervals de animação
  const processingInterval = useRef(null);
  // Ref para selectedBook — evita stale closure nos callbacks de pagamento
  const selectedBookRef = useRef(null);
  useEffect(() => {
    selectedBookRef.current = modalState.selectedBook;
  }, [modalState.selectedBook]);
  
  // Handlers
  const handleEmailChange = useCallback((value) => {
    updateModalState({ 
      email: value,
      emailError: '',
      error: ''
    });
  }, [updateModalState]);

  const handlePurchase = useCallback((bookSide) => {
    console.log('🛒 Iniciando compra para:', bookSide);
    
    updateModalState({
      isOpen: true,
      step: 'email',
      selectedBook: bookSide,
      email: '',
      emailError: '',
      error: '',
      processingTimer: 0
    });
  }, [updateModalState]);

  const handleEmailSubmit = useCallback(async () => {
    const emailValidationError = validateEmail(modalState.email);
    
    if (emailValidationError) {
      updateModalState({ emailError: emailValidationError });
      return;
    }

    updateModalState({ isLoading: true, error: '' });

    try {
      // Gera orderId único
      const newOrderId = `ORD-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      console.log('🆔 Novo orderId:', newOrderId);
      
      // Envia para webhook
      const webhookResponse = await sendToWebhook(modalState.email, modalState.selectedBook, newOrderId);
      
      // Atualiza estado com dados do pagamento
      updateModalState({
        step: 'pix',
        orderId: newOrderId,
        qrCodeImage: webhookResponse.qrcode
          ? (webhookResponse.qrcode.startsWith('data:') ? webhookResponse.qrcode : `data:image/png;base64,${webhookResponse.qrcode}`)
          : '',
        pixCode: webhookResponse.pixCode,
        downloadToken: webhookResponse.downloadToken || '',
        isLoading: false
      });
      
      // Inicia polling
      startPolling(
        newOrderId,
        checkPaymentStatus,
        () => handlePaymentApproved(),
        (error) => updateModalState({ error, step: 'pix' }),
        () => updateModalState({ 
          error: 'Tempo limite excedido. Tente novamente.',
          step: 'pix'
        })
      );
      
    } catch (error) {
      updateModalState({
        error: 'Erro ao processar pedido. Tente novamente.',
        isLoading: false
      });
      console.error('Erro no handleEmailSubmit:', error);
    }
  }, [modalState.email, modalState.selectedBook, updateModalState, startPolling]);

  const handlePaymentSuccess = useCallback(() => {
    console.log('🎉 Processamento concluído!');

    const side = selectedBookRef.current;

    updateModalState({
      step: 'success',
      showMessageInput: true
    });

    if (side) {
      setSales(prev => ({ ...prev, [side]: prev[side] + 1 }));
    }
  }, [updateModalState, setSales]);

  // Ref para sempre chamar a versão mais atual de handlePaymentSuccess
  const handlePaymentSuccessRef = useRef(handlePaymentSuccess);
  useEffect(() => {
    handlePaymentSuccessRef.current = handlePaymentSuccess;
  }, [handlePaymentSuccess]);

  const handlePaymentApproved = useCallback(() => {
    console.log('✅ Pagamento aprovado!');

    updateModalState({
      step: 'processing',
      processingTimer: 0
    });

    processingInterval.current = setInterval(() => {
      updateModalState(prev => ({
        processingTimer: prev.processingTimer + 1
      }));
    }, 1000);

    setTimeout(() => {
      if (processingInterval.current) {
        clearInterval(processingInterval.current);
      }
      handlePaymentSuccessRef.current();
    }, PROCESSING_DURATION);
  }, [updateModalState]);

  const copyPixCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(modalState.pixCode);
      const button = document.getElementById('copy-pix-btn');
      if (button) {
        const originalText = button.textContent;
        button.textContent = '✅ Copiado!';
        setTimeout(() => {
          button.textContent = originalText;
        }, COPY_FEEDBACK_DURATION);
      }
    } catch (err) {
      console.error('Erro ao copiar código PIX:', err);
    }
  }, [modalState.pixCode]);

  // Anima placar quando polling detecta venda de outro usuário
  useEffect(() => {
    if (!changedSide) return;
    if (!modalState.isOpen) triggerSaleAnimation(changedSide);
    clearChangedSide();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changedSide]);

  const triggerSaleAnimation = useCallback((side) => {
    if (animatingNumber) return;
    
    setAnimatingNumber({ side, id: Date.now() });
    
    setTimeout(() => {
      setAnimatingNumber(null);
    }, ANIMATION_DURATION);
  }, [animatingNumber]);

  const closeModal = useCallback(() => {
    const wasSuccess = modalState.step === 'success';
    const side = selectedBookRef.current;

    stopPolling();
    if (processingInterval.current) {
      clearInterval(processingInterval.current);
      processingInterval.current = null;
    }
    resetModalState();

    if (wasSuccess && side) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        triggerSaleAnimation(side);
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 4000);
      }, 400);
    }
  }, [stopPolling, resetModalState, modalState.step]);

  // Cleanup geral
  useEffect(() => {

    window.debugFunctions = {
      triggerSaleAnimation,
      incrementSales,
      handlePurchase,
    // Adicione outras funções que quiser testar
    };
    
    return () => {
      if (processingInterval.current) {
        clearInterval(processingInterval.current);
      }
      delete window.debugFunctions;
      stopPolling();
    };
  }, [stopPolling]);

  // Componente de status de conexão
  const ConnectionStatus = () => (
    <div className={`fixed top-4 right-4 z-50 px-3 py-2 rounded-lg text-sm font-medium ${
      isConnected 
        ? 'bg-blue-100 text-blue-800 border border-blue-200' 
        : 'bg-red-100 text-red-800 border border-red-200'
    }`}>
      {isConnected ? (
        <>
          <Wifi className="inline mr-1" size={14} />
          Ao Vivo
        </>
      ) : (
        <>
          <WifiOff className="inline mr-1" size={14} />
          Offline
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* SEO Head */}
      <SEOHead selectedBook={modalState.selectedBook} sales={sales} />

      {/* Status de conexão */}
      <ConnectionStatus />
      
      {/* Header */}
      <header className="bg-white shadow-lg" role="banner">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-400 mb-3 tracking-widest uppercase">⚔️ EbookArena</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              Esquerda vs Direita:<br className="hidden sm:block" /> Qual lado você vai ajudar a <span className="underline decoration-wavy">VENCER</span>?
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Dois ebooks polêmicos. Duas visões opostas do Brasil. A cada venda, seu lado ganha 1 ponto no placar ao vivo.
            </p>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4" role="main">
        {/* Placar */}
        <ScoreBoard
          sales={sales}
          animatingNumber={animatingNumber}
          handlePurchase={handlePurchase}
        />

        {/* Seção dos livros */}
        <BooksSection
          sales={sales}
          handlePurchase={handlePurchase}
        />
      </main>

      {/* Modal de compra */}
      <PurchaseModal
        modalState={modalState}
        updateModalState={updateModalState}
        closeModal={closeModal}
        handleEmailChange={handleEmailChange}
        handleEmailSubmit={handleEmailSubmit}
        copyPixCode={copyPixCode}
        validateEmail={validateEmail}
        sales={sales}
      />

      {showCelebration && <Celebration />}
    </div>
  );
};

export default EbookLandingPageProduction;